/**
 * <EntryReviewButton /> — shared 1-5 star + comment popover for
 * rating a time_entries row. Requires `timesheet.review` permission
 * on the backend; failure surfaces as a toast.
 *
 * Props:
 *   entry        the time_entry row (must carry id · rating? · review_comment?)
 *   onReviewed?  called after a successful review save
 *   compact?     if true → tiny 6x6 icon button (widget style)
 *                if false → normal chip w/ existing stars + label
 */
import { useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

export default function EntryReviewButton({ entry, onReviewed, compact = false }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(entry?.rating || 0);
  const [comment, setComment] = useState(entry?.review_comment || "");
  const [saving, setSaving] = useState(false);

  const reviewed = Boolean(entry?.rating || entry?.review_comment);

  const submit = async () => {
    if (!rating && !comment.trim()) {
      toast.error("Give at least a rating or a comment");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/time-entries/${entry.id}/review`, {
        rating: rating || null,
        review_comment: comment.trim() || null,
      });
      toast.success("Review saved");
      setOpen(false);
      onReviewed?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Review failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {compact ? (
          <button
            type="button"
            className={`inline-flex items-center justify-center w-6 h-6 border ${reviewed ? "border-amber-400 bg-amber-50 text-amber-600" : "border-border text-muted-foreground hover:bg-amber-50 hover:border-amber-400"}`}
            title={entry?.review_comment || "Rate & comment (needs timesheet.review perm)"}
            data-testid={`entry-review-open-${entry.id}`}
          >★</button>
        ) : (
          <button
            type="button"
            className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border rounded-none ${reviewed ? "border-amber-400 bg-amber-50 text-amber-700" : "border-border text-muted-foreground hover:bg-amber-50 hover:border-amber-400"}`}
            title={entry?.review_comment || "Rate & comment (needs timesheet.review perm)"}
            data-testid={`entry-review-open-${entry.id}`}
          >
            {reviewed ? (
              <span className="font-mono">
                {"★".repeat(entry.rating || 0)}{"☆".repeat(5 - (entry.rating || 0))}
              </span>
            ) : (
              <span>★ Rate</span>
            )}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" side="bottom" className="w-72 p-2 rounded-none z-[95]" data-testid={`entry-review-panel-${entry.id}`}>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Review this entry</div>
        <div className="flex gap-0.5 mb-2">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setRating(n)}
              className={`text-lg leading-none ${n <= rating ? "text-amber-500" : "text-muted-foreground/40"}`}
              data-testid={`entry-review-star-${entry.id}-${n}`}
            >★</button>
          ))}
          {rating > 0 && (
            <button
              type="button"
              onClick={() => setRating(0)}
              className="text-[10px] text-muted-foreground hover:text-foreground ml-2"
              data-testid={`entry-review-clear-${entry.id}`}
            >clear</button>
          )}
        </div>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Optional review comment…"
          rows={3}
          className="w-full text-xs px-2 py-1 border border-border bg-background"
          data-testid={`entry-review-comment-${entry.id}`}
        />
        <div className="flex gap-1 justify-end mt-1">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-[10px] px-2 py-1 border border-border hover:bg-muted"
            data-testid={`entry-review-cancel-${entry.id}`}
          >Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="text-[10px] px-2 py-1 bg-primary text-primary-foreground border border-primary hover:opacity-90 disabled:opacity-60"
            data-testid={`entry-review-save-${entry.id}`}
          >{saving ? "…" : "Save review"}</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
